import { FC, useContext, useMemo, useState, type JSX } from "react";
import styles from "./StockCorporateActions.module.scss";
import Module from "@/components/module/Module";
import { getCorporateActionsList } from "@/controllers/stock-corporate-actions";
import { Stock } from "@/models/stock";
import { CorporateActionRow } from "@/models/stock-corporate-actions";
import { MemoizedDataGrid } from "../data-grid/DataGrid";
import { ModuleContext } from "@/lib/Context";
import { Box, Chip } from "@mui/material";
import CorporateActionDetailsModal from "./CorporateActionDetailsModal";

interface PropsTypes {
    stock: Stock;
}

// Fixed family list served by /interface/api/corporate_actions/company/{id}/list.
const CORPORATE_ACTION_FAMILIES: Array<{ key: string; label: string }> = [
    { key: "splits", label: "Splits" },
    { key: "dividends", label: "Dividends" },
    { key: "deals", label: "Deals & Spin-offs" },
    { key: "buybacks", label: "Buybacks" },
    { key: "equity_offerings", label: "Equity Offerings" },
    { key: "lockups", label: "Lock-ups" },
    { key: "indexes", label: "Index Changes" },
];

const ALL_FAMILY_KEYS = CORPORATE_ACTION_FAMILIES.map((family) => family.key);

const StockCorporateActions: FC<PropsTypes> = ({ stock }) => {
    const { obfuscated } = useContext(ModuleContext);

    const [activeFamilies, setActiveFamilies] = useState<string[]>(ALL_FAMILY_KEYS);
    const [gridFilters, setGridFilters] = useState<JSX.Element>(null);
    const [detailsRow, setDetailsRow] = useState<CorporateActionRow | null>(null);

    const toggleFamily = (key: string) => {
        setActiveFamilies((prev) => {
            const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
            // An empty selection cannot be expressed server-side (it means "all"),
            // so deselecting the last chip restores the full set.
            return next.length ? next : ALL_FAMILY_KEYS;
        });
    };

    // The grid refetches when `data` changes (deep-compared by MemoizedDataGrid), so
    // only include `families` when it actually narrows the feed.
    const gridData = useMemo(() => {
        const allSelected = activeFamilies.length === ALL_FAMILY_KEYS.length;
        return {
            company_id: stock.company_id,
            ...(allSelected ? {} : { families: ALL_FAMILY_KEYS.filter((key) => activeFamilies.includes(key)).join(",") }),
        };
    }, [stock.company_id, activeFamilies]);

    const familyChips = (
        <Box className={styles.chips}>
            {CORPORATE_ACTION_FAMILIES.map(({ key, label }) => {
                const active = activeFamilies.includes(key);
                return <Chip key={key} size="small" clickable variant={active ? "filled" : "outlined"} color={active ? "primary" : "default"} label={label} onClick={() => toggleFamily(key)} />;
            })}
        </Box>
    );

    return (
        <div className={styles.StockCorporateActions}>
            <div className={styles.content}>
                <Module
                    obfuscated={obfuscated}
                    title="Corporate Actions"
                    help={{ moduleId: "corporate_actions", modulePage: "V2_stock_corporate_actions" }}
                    filters={
                        <>
                            {familyChips} {gridFilters}
                        </>
                    }
                    allowFullscreen
                    feedback
                >
                    <MemoizedDataGrid fetcher={getCorporateActionsList} id={stock.stock_id} data={gridData} setFilters={setGridFilters} onRowClick={(row: CorporateActionRow) => setDetailsRow(row)} noRowsMessage="No corporate actions found" />
                </Module>
            </div>
            <CorporateActionDetailsModal row={detailsRow} onClose={() => setDetailsRow(null)} />
        </div>
    );
};

export default StockCorporateActions;
